import React, { useEffect, useMemo } from "react";
import {
  Radio,
  Stack,
  Box,
  RadioGroup,
  Divider,
  Columns,
  Column,
  Text,
} from "@origin-digital/ods-core";
import { AccountPayer, EnergyType, FormValues, GasOptionEnum } from "../types";
import { useFormikContext } from "formik";
import { accountBlockInitialValues } from "../FormComponents/Validations";
import { GasOptions } from "../FormComponents/GasOptions";
import ReadOnlyFieldWrapper from "../ReadOnlyFieldWrapper";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import { useResponsiveBelowMd } from "src/hooks";

const HomeUserFuelInPremise = ({ isReadOnly }: { isReadOnly?: boolean }) => {
  const { values, setFieldValue, errors } = useFormikContext<FormValues>();
  const { selectedConnections, gasOptions } = values;
  const isBelowMd = useResponsiveBelowMd();

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  const calculatedErrors = useMemo(() => {
    return (
      errors.electricityAccountPayer ||
      errors.gasLine?.accountPayer ||
      errors.gasMeter?.accountPayer ||
      errors.both?.accountPayer
    );
  }, [errors]);

  useEffect(() => {
    if (values.premiseAddress.state === "ACT") {
      setFieldValue("gasOptions", [GasOptionEnum.BOTH]);
    }
  }, [values.premiseAddress.state]);

  const updateFuelPayers = (accountPayer: AccountPayer) => {
    selectedConnections.forEach((connection) => {
      if (connection === "Electricity") {
        setFieldValue("electricityAccountPayer", accountPayer);
      }
      if (connection === "Gas") {
        gasOptions.forEach((option) => {
          setFieldValue(`${option}.accountPayer`, accountPayer);
        });
      }
      if (connection === "Internet") {
        setFieldValue("internetAccountPayer", accountPayer);
      }
    });
  };

  const calculatedValue = accountPayers.includes(AccountPayer.SOMEONE_ELSE)
    ? AccountPayer.SOMEONE_ELSE
    : AccountPayer.COMPANY;
  const valueToDisplay = accountPayers.includes(AccountPayer.SOMEONE_ELSE)
    ? "Customer"
    : "Me/My Company";

  const showGasOptions =
    values.selectedConnections.includes("Gas") &&
    values.premiseAddress.state !== "ACT";

  return (
    <Stack>
      {showGasOptions && (
        <Box marginBottom="xxlarge">
          <GasOptions homeUser={true} />
        </Box>
      )}
      {isReadOnly ? (
        <ReadOnlyFieldWrapper value={true}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
          >
            {/* Fields */}
            <Column width="2/3">
              <FormFieldLayout multipleFields>
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Who’s responsible for the accounts?
                    </Text>
                    <Text>{valueToDisplay}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </FormFieldLayout>
            </Column>
            {/* Fields */}
            {/* HelperText */}
            <Column width="1/3">
              <Box display="flex" alignItems="stretch" height="full">
                {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                {!isBelowMd && (
                  <Box
                    style={{ borderLeft: "1px solid #E3E3E3" }}
                    paddingRight="large"
                    height="full"
                  />
                )}
                {/* End Faux vertical <Divider> */}
                <Box alignSelf="center" marginTop="xsmall">
                  <Stack
                    space={{
                      xs: "small",
                      md: "medium",
                    }}
                  >
                    <Text variant="body-small" tone="neutralLight">
                      If you need to assign different account holders for
                      electricity, natural gas and internet, you will need to
                      raise a separate requests.
                    </Text>
                    {values.selectedConnections.includes(
                      "Internet" as EnergyType
                    ) && (
                      <Text variant="body-small" tone="neutralLight">
                        The account holder will receive an email to compare our
                        internet plans. There's no obligation for them to sign
                        up with Origin.
                      </Text>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Column>
            {/* End HelperText */}
          </Columns>
        </ReadOnlyFieldWrapper>
      ) : (
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
        >
          <Columns
            collapseBelow="md"
            space={{
              xs: "xxsmall",
              md: "small",
            }}
            alignY="stretch"
          >
            <Column width="2/3">
              <FormFieldLayout multipleFields>
                <RadioGroup
                  id="combinedAccountPayer"
                  name="combinedAccountPayer"
                  label="Who's responsible for the account(s)?"
                  error={!!calculatedErrors}
                  helperText={calculatedErrors}
                  value={calculatedValue}
                  onChange={(e) => {
                    Object.keys(accountBlockInitialValues).forEach((k) => {
                      setFieldValue(
                        `${k}`,
                        accountBlockInitialValues[
                          k as keyof typeof accountBlockInitialValues
                        ]
                      );
                    });
                    updateFuelPayers(e.target.value as AccountPayer);
                  }}
                >
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    flexDirection={{
                      xs: "column",
                      sm: "column",
                    }}
                  >
                    <Radio
                      id="my-company"
                      label={"Me/My company"}
                      helperText="Will be responsible for any charges until the account is closed."
                      value={AccountPayer.COMPANY}
                    />
                    <Radio
                      id="customer"
                      label="Someone else"
                      helperText="Will be responsible for any charges until the account is closed."
                      value={AccountPayer.SOMEONE_ELSE}
                    />
                  </Box>
                </RadioGroup>
              </FormFieldLayout>
            </Column>
            <Column width="1/3">
              <Box display="flex" alignItems="stretch" height="full">
                {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                {!isBelowMd && (
                  <Box
                    style={{ borderLeft: "1px solid #E3E3E3" }}
                    paddingRight="large"
                    height="full"
                  />
                )}
                {/* End Faux vertical <Divider> */}
                <Box alignSelf="center" marginTop="xsmall">
                  <Stack
                    space={{
                      xs: "small",
                      md: "medium",
                    }}
                  >
                    <Text variant="body-small" tone="neutralLight">
                      If you need to assign different account holders for
                      electricity, natural gas and internet, you will need to
                      raise a separate requests.
                    </Text>
                    {values.selectedConnections.includes(
                      "Internet" as EnergyType
                    ) && (
                      <Text variant="body-small" tone="neutralLight">
                        The account holder will receive an email to compare our
                        internet plans. There's no obligation for them to sign
                        up with Origin.
                      </Text>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Column>
          </Columns>
        </Stack>
      )}
    </Stack>
  );
};

export default HomeUserFuelInPremise;
